/**
 * Utility functions for handling file uploads (images, videos, documents, pdfs)
 * with AWS S3 Presigned URL direct uploads and backend storage fallback.
 */

import { apiClient, API_BASE_URL } from '../lib/api';
import toast from 'react-hot-toast';

export const MAX_ATTACHMENT_SIZE_BYTES = 32 * 1024 * 1024; // 32 MB
export const MAX_ATTACHMENT_SIZE_MB = 32;

export function formatFileSize(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

export type MediaCategory = 'image' | 'video' | 'pdf' | 'document' | 'other';

export interface UploadedFileItem {
  id: string;
  name: string;
  size: string;
  sizeBytes: number;
  type: string;
  category: MediaCategory;
  fileUrl: string;
  dataUrl: string;
  previewUrl?: string;
  thumbnailUrl?: string;
  isImage: boolean;
  isVideo: boolean;
  isPdf: boolean;
  isDocument: boolean;
  s3Key?: string;
  s3Bucket?: string;
  uploadedAt?: string;
  uploadProgress?: number;
}

export function resolveStorageUrl(url?: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }
  const cleanBase = (API_BASE_URL || window.location.origin).replace(/\/$/, '');
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${cleanBase}${cleanUrl}`;
}

export function detectCategory(filename: string, mimeType?: string): MediaCategory {
  const mime = mimeType?.toLowerCase() || '';
  const name = filename.toLowerCase();

  if (mime.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif|svg|bmp|ico)$/i.test(name)) {
    return 'image';
  }
  if (mime.startsWith('video/') || /\.(mp4|webm|mov|mkv|avi|m4v)$/i.test(name)) {
    return 'video';
  }
  if (mime === 'application/pdf' || /\.pdf$/i.test(name)) {
    return 'pdf';
  }
  if (/\.(doc|docx|xls|xlsx|ppt|pptx|txt|csv|json|zip|tar|gz|log|xml)$/i.test(name)) {
    return 'document';
  }
  return 'other';
}

/**
 * Generates an image thumbnail snapshot from a video file
 */
export function generateVideoThumbnail(file: File): Promise<string> {
  return new Promise((resolve) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      const url = URL.createObjectURL(file);
      video.src = url;

      video.onloadeddata = () => {
        video.currentTime = Math.min(1.0, video.duration / 2 || 0.5);
      };

      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = Math.min(320, video.videoWidth || 320);
          canvas.height = Math.round(canvas.width * ((video.videoHeight || 180) / (video.videoWidth || 320)));
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbUrl = canvas.toDataURL('image/jpeg', 0.7);
            URL.revokeObjectURL(url);
            resolve(thumbUrl);
            return;
          }
        } catch {
          // Ignore canvas security or decode errors
        }
        URL.revokeObjectURL(url);
        resolve('');
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve('');
      };
    } catch {
      resolve('');
    }
  });
}

export interface UploadOptions {
  module: 'assets' | 'support';
  subFolder?: string;
  assetId?: string;
  assetTag?: string;
  kind?: string;
  onProgress?: (percent: number) => void;
}

/**
 * High-level upload function that handles direct S3 upload via presigned URL,
 * with automatic fallback to backend endpoint upload.
 */
export async function uploadFileToStorage(
  file: File,
  options: UploadOptions
): Promise<UploadedFileItem> {
  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    throw new Error(`File "${file.name}" (${formatFileSize(file.size)}) exceeds maximum allowed size of ${MAX_ATTACHMENT_SIZE_MB} MB`);
  }

  const category = detectCategory(file.name, file.type);
  const isImage = category === 'image';
  const isVideo = category === 'video';
  const isPdf = category === 'pdf';
  const isDocument = isPdf || category === 'document';

  let thumbnail = '';
  if (isVideo) {
    try {
      thumbnail = await generateVideoThumbnail(file);
    } catch {
      // ignore
    }
  }

  let dataUrl = '';
  if (file.size <= MAX_ATTACHMENT_SIZE_BYTES) {
    // Generate dataUrl for client-side instant preview
    try {
      dataUrl = await readFileAsDataUrl(file);
    } catch {
      // ignore
    }
  }

  try {
    // Step 1: Request presigned URL from backend service
    const presignedRes = await apiClient.post(`/${options.module}/storage/presigned-url`, {
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      subFolder: options.subFolder || (options.module === 'assets' ? 'vault' : 'tickets'),
    });

    const presignedData = presignedRes.data?.data || presignedRes.data;

    // Step 2: Direct browser PUT to S3 if presigned S3 url available
    if (presignedData?.direct && presignedData?.uploadUrl) {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedData.uploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

        if (xhr.upload && options.onProgress) {
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const pct = Math.round((e.loaded / e.total) * 100);
              options.onProgress?.(pct);
            }
          };
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`S3 PUT failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error during S3 upload'));
        xhr.send(file);
      });

      return {
        id: `upload-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        name: file.name,
        size: formatFileSize(file.size),
        sizeBytes: file.size,
        type: file.type || 'application/octet-stream',
        category,
        fileUrl: presignedData.fileUrl,
        dataUrl: dataUrl || presignedData.fileUrl,
        previewUrl: isImage ? (dataUrl || presignedData.fileUrl) : (thumbnail || undefined),
        thumbnailUrl: thumbnail || undefined,
        isImage,
        isVideo,
        isPdf,
        isDocument,
        s3Key: presignedData.s3Key,
        s3Bucket: presignedData.s3Bucket,
        uploadedAt: new Date().toISOString(),
        uploadProgress: 100,
      };
    }
  } catch (directErr) {
    console.warn('Direct S3 upload unavailable or failed, falling back to backend storage endpoint:', directErr);
  }

  // Step 3: Fallback - Upload directly through backend service endpoint
  if (!dataUrl) {
    dataUrl = await readFileAsDataUrl(file);
  }

  const uploadRes = await apiClient.post(`/${options.module}/storage/upload`, {
    dataUrl,
    filename: file.name,
    mimeType: file.type || 'application/octet-stream',
    subFolder: options.subFolder || (options.module === 'assets' ? 'vault' : 'tickets'),
    assetId: options.assetId,
    assetTag: options.assetTag,
    kind: options.kind,
  });

  const uploadData = uploadRes.data?.data || uploadRes.data;
  const rawFileUrl = uploadData.fileUrl || `/uploads/${options.module}/${options.subFolder || 'general'}/${file.name}`;
  const fullFileUrl = resolveStorageUrl(rawFileUrl);
  const formattedName = uploadData.fileName || file.name;

  return {
    id: uploadData.id || `upload-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    name: formattedName,
    size: formatFileSize(file.size),
    sizeBytes: file.size,
    type: file.type || 'application/octet-stream',
    category,
    fileUrl: fullFileUrl,
    dataUrl: dataUrl || fullFileUrl,
    previewUrl: isImage ? (dataUrl || fullFileUrl) : (thumbnail || undefined),
    thumbnailUrl: thumbnail || undefined,
    isImage,
    isVideo,
    isPdf,
    isDocument,
    s3Key: uploadData.s3Key,
    s3Bucket: uploadData.s3Bucket,
    uploadedAt: new Date().toISOString(),
    uploadProgress: 100,
  };
}

/**
 * Processes selected files from an input or drag-and-drop
 */
export async function processSelectedFiles(
  files: FileList | File[],
  targetModule: 'assets' | 'support' = 'support',
  subFolder?: string,
  extraOptions?: { assetId?: string; assetTag?: string; kind?: string }
): Promise<UploadedFileItem[]> {
  const fileArray = Array.from(files);
  const results: UploadedFileItem[] = [];

  for (const file of fileArray) {
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      toast.error(`"${file.name}" (${formatFileSize(file.size)}) exceeds the maximum allowed size of ${MAX_ATTACHMENT_SIZE_MB} MB.`);
      continue;
    }

    try {
      const item = await uploadFileToStorage(file, {
        module: targetModule,
        subFolder,
        assetId: extraOptions?.assetId,
        assetTag: extraOptions?.assetTag,
        kind: extraOptions?.kind,
      });
      results.push(item);
    } catch (err: any) {
      console.error(`Failed to upload ${file.name}:`, err);
      // If error is size limit, notify and don't create fallback
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        toast.error(err?.message || `File exceeds ${MAX_ATTACHMENT_SIZE_MB} MB limit`);
        continue;
      }
      // Create local fallback representation so user doesn't lose work
      const dataUrl = await readFileAsDataUrl(file).catch(() => '');
      const category = detectCategory(file.name, file.type);
      results.push({
        id: `file-err-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        name: file.name,
        size: formatFileSize(file.size),
        sizeBytes: file.size,
        type: file.type || 'application/octet-stream',
        category,
        fileUrl: dataUrl || '',
        dataUrl: dataUrl || '',
        previewUrl: category === 'image' ? dataUrl : undefined,
        isImage: category === 'image',
        isVideo: category === 'video',
        isPdf: category === 'pdf',
        isDocument: category === 'pdf' || category === 'document',
        uploadedAt: new Date().toISOString(),
      });
    }
  }

  return results;
}

export function downloadMediaFile(fileUrl: string, fileName: string) {
  try {
    const link = document.createElement('a');
    link.href = resolveStorageUrl(fileUrl);
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    console.error('Download failed:', e);
  }
}
